/**
 * Shared shapes for the member dashboard's pure builders.
 *
 * Nothing here imports Firebase or React. Every builder in this directory takes
 * plain data and returns plain data, so the whole decision layer of the
 * dashboard is testable without fixtures, mocks, or an emulator.
 */

/**
 * Whether a source loaded, loaded with nothing in it, or failed to load.
 *
 * This distinction is the reason the directory exists. The old Home page
 * guarded each read with `.catch(() => [])`, which collapses "you have no lab
 * results" and "the lab read was denied" into the same empty array — so a rules
 * regression would render as a confident "0 biomarkers outside range" rather
 * than as a gap. Blocks branch on this: a `failed` source hides its block or
 * its chip, and **never renders a zero**.
 */
export type SourceStatus = 'ok' | 'empty' | 'failed';

/**
 * State of a member's clinical-history interview booking.
 *
 * Declared here rather than in the service so the pure builders can use it
 * without pulling Firebase into their imports. It was previously written out
 * by hand in five places, which is why adding 'cancelled' broke four of them.
 *
 * 'pending' is retained only for bookings created before auto-confirm; nothing
 * writes it now.
 */
export type BookingStatus = 'none' | 'pending' | 'confirmed' | 'cancelled';

/** The booking shape the dashboard builders consume. */
export interface BookingSummary {
  status: BookingStatus;
  bookingId?: string | null;
  startAt: Date | null;
  /** Absent on bookings made before the time was denormalised. */
  endAt?: Date | null;
  doctorName: string | null;
  meetingLink?: string | null;
}

export interface DashboardSources {
  labs: SourceStatus;
  wearable: SourceStatus;
  plan: SourceStatus;
  reports: SourceStatus;
  scheduledTests: SourceStatus;
  conversations: SourceStatus;
  intake: SourceStatus;
  dailyBite: SourceStatus;
  careTeam: SourceStatus;
  booking: SourceStatus;
}

export const INITIAL_SOURCES: DashboardSources = {
  labs: 'empty',
  wearable: 'empty',
  plan: 'empty',
  reports: 'empty',
  scheduledTests: 'empty',
  conversations: 'empty',
  intake: 'empty',
  dailyBite: 'empty',
  careTeam: 'empty',
  booking: 'empty',
};

/** A source is usable when it loaded — whether or not it had anything in it. */
export function isUsable(status: SourceStatus): boolean {
  return status !== 'failed';
}

/** A source has content we can count or compare against. */
export function hasContent(status: SourceStatus): boolean {
  return status === 'ok';
}
